<template>
  <Tile class="data-tile" ref="dataTile">
    <Loader :loading="loading" :error="error">
      <TileContent
        :title="title"
        :tooltip="tooltip"
        @iconClick="emitIconClick"
      >
        <template v-slot:chart>
          <ChartOverlay :message="errorMessage">
            <DualAxisBarAndLine
              :data="sortedChartData"
              :yMetrics="chartMetrics"
              :xDimension="chartDimension"
              :chartDefaults="chartDefaults"
              :chartWidth="chartWidth"
              :chartHeight="chartHeight"
              :useShortLabel="true"
              :isSorted="true"
              :loading="loading"
            />
          </ChartOverlay>
        </template>
        <template v-slot:table>
          <Table
            :hide-default-footer="true"
            :outlined="true"
            :data="data"
            :customColumns="customColumns"
            :loading="loading"
            :initialSortKey="initialSort"
            :initialSortDesc="true"
            @updateSortKey="updateSortKey"
            @updateSortOrder="updateSortOrder"
            dense
          />
        </template>
      </TileContent>
    </Loader>
  </Tile>
</template>

<script>
import { mapState } from 'vuex';
import Tile from './Tile.vue';
import TileContent from './TileContent.vue';
import Table from './Table.vue';
import Loader from './Charting/Shared/Loader.vue';
import ChartOverlay from './ChartOverlay';
import DualAxisBarAndLine from '../Common/Charting/Shared/Charts/DualAxisBarAndLine.vue';
import { getReportFieldFromValue } from '../../../services/dashboard/reportFieldService';

export default {
  components: {
    TileContent,
    Table,
    DualAxisBarAndLine,
    Tile,
    Loader,
    ChartOverlay,
  },
  props: {
    data: Array,
    title: String,
    metrics: Array,
    dimensions: Array,
    loading: Boolean,
    error: Boolean,
    initialSort: String,
    tooltip: String,
  },
  data() {
    return {
      chartDefaults: {
        dataWidth: 40,
        xLabelPadding: 16,
        yLabelPadding: 8,
        yLabelWidth: 60,
        fontSize: 12,
        margin: {
          top: 10,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      chartWidth: 0,
      chartHeight: 330,
      localSortKey: this.initialSort,
      localSortOrder: 1,
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      activeView: state => state.main.activeView,
    }),
    dimensionReportField() {
      const dimension = this.dimensions[0];

      return getReportFieldFromValue(this.allReportFields, dimension);
    },
    metricsReportFields() {
      return this.metrics.map(m => getReportFieldFromValue(this.allReportFields, m));
    },
    chartDimension() {
      if (this.dimensions[0] === 'cartName' && this.activeView === 'Sales Insights') return 'retailer';

      return this.dimensionReportField.snake;
    },
    chartMetrics() {
      const metricsSnake = this.metricsReportFields.map(rf =>  rf.snake);
      const lastMetricIdx = metricsSnake.length - 1;

      return {
        left: metricsSnake[0],
        right: metricsSnake[lastMetricIdx],
      };
    },
    customColumns() {
      const dimensionColumn = {
        value: this.chartDimension,
        name: this.firstColumnName,
        align: 'left',
      };

      const metricColumns = this.metricsReportFields.map((m, i) => {

        const lastColumn = i === this.metricsReportFields.length - 1;

        return {
          value: m.snake,
          name: m.key,
          align: lastColumn ? 'right' : 'left',
        };
      });

      return [dimensionColumn].concat(metricColumns);
    },
    firstColumnName() {
      // column name is singular version of title
      if (this.title === 'Categories') return  'Category';

      return this.title.slice(0, -1);
    },
    chartData() {
      if (this.chartDimension === 'experience_name_w_link') {
        return this.data.map(d => {
          /**
           * experience_name_w_link is
           * a JSON string for a JavaScript array with 2 values:
           * 1. A string for experience name
           * 2. A string for the experience ID
           */
          const name = JSON.parse(d['experience_name_w_link'])[0];

          return { ...d, ['experience_name_w_link']: name };
        });
      }

      return this.data;
    },
    sortedChartData() {
      return [...this.chartData].sort((a, b) => {
        a = a[this.localSortKey];
        b = b[this.localSortKey];
        if (a === b) return 0;
        else
          return (a > b ? -1 : 1) * this.localSortOrder;
      });
    },
    errorMessage() {
      return this.data && !this.loading && this.data.length === 0
        ? 'No Data'
        : undefined;
    },
  },
  methods: {
    updateSortKey(val) {
      this.localSortKey = val;
    },
    updateSortOrder(val) {
      this.localSortOrder = val;
    },
    emitIconClick() {
      this.$emit('iconClick', {
        title: this.title,
        tableData: this.data,
      });
    },
    handleResize() {
      const tileWidth = this.$refs.dataTile.$el.clientWidth;

      // leave room for 16 px margin on each side
      this.chartWidth = tileWidth - 32;
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.handleResize();
  },
};
</script>

<style scoped>
.data-tile {
  width: 500px;
  min-height: 600px;
}
</style>

